import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@ferro/ui';
import type { ClientConfig } from '@ferro/config';
import { useAuth } from '../contexts/AuthContext.js';
import { roleHasPermission } from '../lib/rbac-matrix.js';
import {
  createIncident,
  fetchCustomerDogs,
  fetchCustomers,
  readSession,
  INCIDENT_KINDS,
  INCIDENT_SEVERITIES,
  type AdminDog,
  type CustomerSummary,
  type IncidentKind,
  type IncidentSeverity,
} from '../lib/api.js';
// Sawyer 12 incident-wording-pet-notes (F3) — the owner's word for the log.
import { useTenantVocabulary } from '../lib/useTenantVocabulary.js';

type IncidentNewProps = { config: ClientConfig };

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const KIND_LABEL: Record<IncidentKind, string> = {
  bite: 'Bite',
  injury: 'Injury',
  escape: 'Escape',
  illness: 'Illness',
  altercation: 'Altercation',
  property_damage: 'Property damage',
  in_heat: 'In heat',
  other: 'Other',
};

const MAX_PHOTO_BYTES = 2_500_000;

// Read a File into a FULL data URL (keeps the `data:<mime>;base64,` prefix) so
// the stored string is directly renderable as <img src> on the detail page.
function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

export function IncidentNew({ config }: IncidentNewProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const navigate = useNavigate();
  const { incident: noun } = useTenantVocabulary(apiUrl);
  const { staff } = useAuth();
  // Mirror the server permission, not a role literal (matches Staff.tsx).
  const canWriteOwnerOnly = roleHasPermission(
    staff?.role ?? null,
    'incidents.owner-only:write:all',
  );

  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [dogs, setDogs] = useState<AdminDog[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [dogId, setDogId] = useState('');
  const [kind, setKind] = useState<IncidentKind>('injury');
  const [severity, setSeverity] = useState<IncidentSeverity>('low');
  const [narrative, setNarrative] = useState('');
  const [photoB64, setPhotoB64] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [ownerNotes, setOwnerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Customer picker source — searchable (debounced) so HTHD's full book is
  // reachable, not just the first 100 most-recent (server caps limit at 100).
  useEffect(() => {
    const { token } = readSession();
    if (!token) return;
    const handle = setTimeout(() => {
      void fetchCustomers(apiUrl, token, {
        limit: 100,
        ...(customerSearch.trim() ? { search: customerSearch.trim() } : {}),
      })
        .then((r) => setCustomers(r.customers))
        .catch(() => {
          /* a load failure here surfaces as an empty picker; submit still guards */
        });
    }, 250);
    return () => clearTimeout(handle);
  }, [apiUrl, customerSearch]);

  // Dog picker depends on the selected customer.
  useEffect(() => {
    const { token } = readSession();
    if (!token || !customerId) {
      setDogs([]);
      setDogId('');
      return;
    }
    void fetchCustomerDogs(apiUrl, token, customerId)
      .then((r) => setDogs(r.dogs))
      .catch(() => setDogs([]));
  }, [apiUrl, customerId]);

  const handlePhoto = async (file: File | undefined): Promise<void> => {
    setPhotoError(null);
    if (!file) {
      setPhotoB64(null);
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError('Photo must be under 2.5 MB.');
      setPhotoB64(null);
      return;
    }
    try {
      setPhotoB64(await readAsDataUrl(file));
    } catch {
      setPhotoError('Could not read that photo.');
      setPhotoB64(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    if (!customerId || !dogId || !narrative.trim()) {
      setError('Customer, dog, and a description are required.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const { token } = readSession();
    if (!token) {
      setError('Not authenticated');
      setSubmitting(false);
      return;
    }
    try {
      await createIncident(apiUrl, token, {
        dogId,
        kind,
        severity,
        narrative: narrative.trim(),
        ...(photoB64 ? { photoUrls: [photoB64] } : {}),
        ...(canWriteOwnerOnly && ownerNotes.trim()
          ? { ownerNotes: ownerNotes.trim() }
          : {}),
      });
      navigate('/incidents');
    } catch (err) {
      setError(err instanceof Error ? err.message : noun.submitFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="ferro-admin-page">
      <header className="ferro-detail__header">
        <h1 className="ferro-section__title">{noun.newTitle}</h1>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => navigate('/incidents')}
        >
          ← Back
        </button>
      </header>

      <form className="ferro-form" onSubmit={handleSubmit} noValidate>
        <label className="ferro-field">
          <span className="ferro-field__label">Find customer</span>
          <input
            type="search"
            className="ferro-field__input"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            placeholder="Search by name, phone, email, or pet…"
          />
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Customer</span>
          <select
            className="ferro-field__input"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            required
          >
            <option value="">Select customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Dog</span>
          <select
            className="ferro-field__input"
            value={dogId}
            onChange={(e) => setDogId(e.target.value)}
            disabled={!customerId}
            required
          >
            <option value="">
              {customerId ? 'Select dog…' : 'Choose a customer first'}
            </option>
            {dogs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Type</span>
          <select
            className="ferro-field__input"
            value={kind}
            onChange={(e) => setKind(e.target.value as IncidentKind)}
          >
            {INCIDENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Severity</span>
          <select
            className="ferro-field__input"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
          >
            {INCIDENT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL[s]}
              </option>
            ))}
          </select>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">What happened</span>
          <textarea
            className="ferro-field__input ferro-field__input--textarea"
            rows={5}
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            required
            placeholder={`Describe the ${noun.oneLower}: who, what, when, and any action taken.`}
          />
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Photo (optional)</span>
          <input
            type="file"
            accept="image/*"
            className="ferro-field__input"
            onChange={(e) => void handlePhoto(e.target.files?.[0])}
          />
        </label>
        {photoError ? <Alert variant="error">{photoError}</Alert> : null}
        {photoB64 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src={photoB64}
              alt="Attached preview"
              style={{ maxWidth: 120, borderRadius: 'var(--ferro-radius, 8px)' }}
            />
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setPhotoB64(null)}
            >
              Remove photo
            </button>
          </div>
        ) : null}

        {canWriteOwnerOnly ? (
          <label className="ferro-field">
            <span className="ferro-field__label">Internal note (business owners only)</span>
            <textarea
              className="ferro-field__input ferro-field__input--textarea"
              rows={3}
              value={ownerNotes}
              onChange={(e) => setOwnerNotes(e.target.value)}
              placeholder="Liability / legal follow-up. Only business owners see this."
            />
          </label>
        ) : null}

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="ferro-form__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => navigate('/incidents')}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary"
            disabled={submitting}
          >
            {submitting ? 'Saving…' : noun.submit}
          </button>
        </div>
      </form>
    </section>
  );
}
